import { useMarkupContext } from "@context-providers/markup/markup-context";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { IElementType } from "@faro-lotv/ielement-types";
import { useProjectMarkupUpdate } from "@hooks/project-markups/use-project-markup-update";
import { getMarkupStatusValue } from "@pages/project-details/project-markups/status/markup-status-utils";
import { removeIElements, setIElements } from "@store/i-elements/i-elements-slice-thunk";
import { markupsTemplateIdsSelector } from "@store/markups/markups-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { useRef } from "react";

/** Returns the store action that applies the status mutation to the markup */
function getStatusStoreAction(markup, mutation, statusTemplateId, userId) {
  if ("newElement" in mutation) {
    const newElement = mutation.newElement;
    const newItem = {
      ...newElement,
      id: newElement.id,
      rootId: newElement.rootId,
      name: newElement.name,
      templateId: statusTemplateId,
      type: IElementType.dropDownMarkupField,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      root_Id: newElement.rootId,
      createdBy: userId,
      createdAt: mutation.createdAt,
      modifiedBy: userId,
      modifiedAt: mutation.createdAt
    };
    return setIElements([newItem]);
  }
  const currentItem = markup.status;
  if (!currentItem) {
    throw new Error("Item is required but was undefined.");
  }
  if ("cascadeLinks" in mutation) {
    return removeIElements([currentItem]);
  }
  if ("value" in mutation) {
    const updatedItem = {
      ...currentItem,
      value: mutation.value,
      modifiedBy: userId,
      modifiedAt: mutation.createdAt
    };
    return setIElements([updatedItem]);
  }
}

/**
 * Returns a function that changes the status of a markup in the backend and then in the store.
 * Errors are reported with a toast, so it never rejects.
 */
export function useChangeMarkupStatus() {
  const {
    projectId,
    setIsMarkupUpdating
  } = useMarkupContext();
  const {
    updateMarkupStatus
  } = useProjectMarkupUpdate({
    projectId
  });
  const {
    handleErrorWithToast
  } = useErrorContext();
  const dispatch = useAppDispatch();
  const templateIds = useAppSelector(markupsTemplateIdsSelector);
  const currentUser = useAppSelector(currentUserSelector);
  // Several markups can be moved at once on the board, so updating ends with the last one
  const pendingChangesCount = useRef(0);
  async function changeStatus(markup, newStatus) {
    if (!templateIds || !currentUser) {
      throw new Error("Expected the project template and the current user");
    }

    // Undefined when the request failed, which is already reported with a toast
    const mutations = await updateMarkupStatus({
      markup,
      newStatus,
      statusTemplateId: templateIds.statusTemplateId
    });
    const action = mutations?.[0] && getStatusStoreAction(markup, mutations[0], templateIds.statusTemplateId, currentUser.identity);
    if (action) {
      await dispatch(action);
    }
  }
  return async (markup_0, newStatus_0) => {
    if (getMarkupStatusValue(markup_0.status?.value) === newStatus_0) {
      return;
    }
    pendingChangesCount.current++;
    setIsMarkupUpdating(true);
    try {
      await changeStatus(markup_0, newStatus_0);
    } catch (error) {
      handleErrorWithToast({
        id: `changeMarkupStatus-${Date.now().toString()}`,
        title: "Failed to change the status of the annotation.",
        error
      });
    } finally {
      pendingChangesCount.current--;
      if (pendingChangesCount.current === 0) {
        setIsMarkupUpdating(false);
      }
    }
  };
}